import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    TablePagination,
    Skeleton,
    Box,
    Button,
    Typography,
    Link as MuiLink,
    Stack,
    Chip,
    CircularProgress,
    Tooltip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@mui/material';
import { MdEdit, MdOutlineCloudUpload, MdSearch } from 'react-icons/md';
import { RiDownloadLine } from "react-icons/ri";

import { fetchGet, get, post, put } from '../utils/http';
import { FullNodeProp } from '../utils/types';
import UploadSlide from '../component/userDirectory/helper/UploadSlide';
import { isPrivate } from '../utils/config';

interface Image {
    id: number;
    name: string;
    slide_type: string
    patient_id: number;
    patient_name: string;
    patient_code: string;
    status: string;
    report: boolean;
    report_url: string;
}

const SimpleDashboard = () => {
    const [slideList, setSlideList] = useState<Image[]>([]);
    const [selectedNode, setSelectedNode] = useState<FullNodeProp | null>(null);
    const [uploadOpen, setUploadOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    
    // Track ID of the currently downloading PDF instead of a global boolean
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const [page, setPage] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [appliedSearch, setAppliedSearch] = useState<string>('');

    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [editingSlide, setEditingSlide] = useState<Image | null>(null);
    const [editFormData, setEditFormData] = useState({ patient_id: 0, patient_code: '', patient_name: '' });
    const [isSubmittingEdit, setIsSubmittingEdit] = useState<boolean>(false);

    const rowCount = 100;
    const nameParts = localStorage.getItem("fullName")?.split(' ') || []
    const userName = (nameParts[0] || 'Dr.') + " " + (nameParts[2] || 'User')
    const userId: number = Number(localStorage.getItem('user_id') || '1')

    const fetchMainTreeData = useCallback(async () => {
        try {
            const resObj = await get(`user-tree/user/${userId}`);
            if (resObj && 'My Folder/' in resObj) {
                const mainNode = { ...resObj['My Folder/'], name: 'My Folder/' };
                setSelectedNode(mainNode);
            }
        } catch (err) {
            console.error("Failed to fetch tree data:", err);
        }
    }, [userId]);

    useEffect(() => {
        fetchMainTreeData();
    }, [fetchMainTreeData]);

    const fetchSlideList = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await post('Images/simple-user', {
                user_id: userId,
                page: page,
                rows: rowCount,
                search: appliedSearch
            });
            if (res) {
                setSlideList(res.images || []);
                setTotal(res.total || 0);
            }
        } catch (err) {
            console.error("Failed to fetch slide list:", err);
        } finally {
            setIsLoading(false);
        }
    }, [userId, page, rowCount, appliedSearch]);

    useEffect(() => {
        fetchSlideList();
    }, [fetchSlideList]);

    const handleExecuteSearch = () => {
        setAppliedSearch(searchTerm);
        setPage(0);
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleExecuteSearch();
        }
    }

    const handleUploadClose = () => setUploadOpen(false);
    
    const saveFile = () => {
        if (page === 0) fetchSlideList()
        else setPage(0)
    };
    
    const saveMounts = () => {/* --- */};

    const handleOpenEdit = (slide: Image) => {
        setEditingSlide(slide);
        setEditFormData({
            patient_id: slide.patient_id || 0,
            patient_code: slide.patient_code || '',
            patient_name: slide.patient_name || ''
        });
        setEditModalOpen(true);
    };

    const handleCloseEdit = () => {
        if (isSubmittingEdit) return; 
        setEditModalOpen(false);
        setEditingSlide(null);
    };

    const handleSaveEdit = async () => {
        if (!editingSlide) return;
        setIsSubmittingEdit(true);
        try {
            if (editFormData.patient_id) {
                await put(`Patients/${editFormData.patient_id}`, {
                    patient_id: editFormData.patient_code,
                    name: editFormData.patient_name,
                    slides: [editingSlide.id]
                });
            } else {
                const newPatient = await post(`Patients`, {
                    patient_id: editFormData.patient_code,
                    name: editFormData.patient_name,
                    slides: [editingSlide.id],
                    created_by: userId
                });
                await put(`Images/${editingSlide.id}`, {
                    patient_id: newPatient.id
                })
            }
            await fetchSlideList();
            handleCloseEdit();
        } catch (error) {
            console.error("Failed to update slide info:", error);
        } finally {
            setIsSubmittingEdit(false);
        }
    };

    const downloadPDF = async (data: Image) => {
        setDownloadingId(data.id);
        try {
            const res = await fetchGet(`Reports/get-pdf/${data.id}`);
            
            if (!res.ok) {
                if (data.report_url) {
                    window.open(data.report_url, '_blank', 'noopener,noreferrer')
                    return
                } else throw new Error('Report not found')
            }
            
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            
            a.href = url;
            const fileName = data.name?.split('/').pop() || `slide_${data.id}`;
            a.download = `${fileName}_report.pdf`;
            
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            // Allow browser time to initiate download before cleanup
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000)
        } catch (err) {
            console.error('Error downloading PDF:', err);
        } finally {
            setDownloadingId(null);
        }
    };

    const renderPagination = (position: 'top' | 'bottom') => {
        return (
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="flex-end"
                alignItems={{ xs: 'flex-end', sm: 'center' }}
                spacing={2}
                sx={{
                    px: 3,
                    py: 1,
                    borderBottom: position === 'top' ? '1px solid' : 'none',
                    borderTop: position === 'bottom' ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}
            >
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowCount}
                    onRowsPerPageChange={() => {}}
                    rowsPerPageOptions={[rowCount]} // Locked row count based on your original request
                    sx={{ borderBottom: 'none' }}
                />
            </Stack>
        );
    };

    const getStatusColor = (status: string) => {
        if (/predicted|completed|analyzed/i.test(status)) return 'success';
        if (/processing|running/i.test(status)) return 'primary';
        if (/pending|queued/i.test(status)) return 'warning';
        if (/failed|error|aborted/i.test(status)) return 'error';
        return 'default';
    };

    return (
        <Box sx={{ bgcolor: '#f4f6f9', minHeight: 'calc(100vh - 56px)', py: { xs: 2, md: 4 } }}>
            <Box sx={{ maxWidth: { xs: '100%', xl: 1800 }, mx: 'auto', px: { xs: 2, sm: 3, lg: 4, xl: 6 } }}>

                <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    justifyContent="space-between" 
                    alignItems={{ xs: 'flex-start', sm: 'center' }} 
                    spacing={3}
                    sx={{ mb: { xs: 2, md: 4 } }}
                >
                    <Box>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 600,
                                color: '#0f172a', 
                                letterSpacing: '-0.01em',
                                mb: 0.5
                            }}
                        >
                            Welcome, {userName}
                        </Typography>

                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 400 }}>
                            Manage and analyze whole slide images
                        </Typography>
                    </Box>

                    {/* Upload Button */}
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 1.5,
                            borderRadius: 4,
                            border: '1px solid', 
                            borderColor: 'grey.200',
                            bgcolor: '#ffffff',
                            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
                            width: { xs: '100%', sm: 'auto' }
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<MdOutlineCloudUpload size={22} />}
                            onClick={() => setUploadOpen(true)}
                            sx={{
                                px: 3,
                                py: 1.2,
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                borderRadius: 3,
                                width: { xs: '100%', sm: 'auto' },
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            Upload WSI Scan
                        </Button>
                    </Paper>
                </Stack>

                {/* Search & Action */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ width: { sm: '100%', md: 'auto' }, mb: 2 }}
                >
                    <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
                        <TextField
                            placeholder="Search ID, Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyDown}
                            size="small"
                            sx={{
                                bgcolor: '#ffffff',
                                borderRadius: 3,
                                width: { xs: '100%', sm: 280, md: 400, xl: 800 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': { borderColor: 'grey.200' },
                                    '&:hover fieldset': { borderColor: 'primary.main' }
                                }
                            }}
                        />
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleExecuteSearch}
                            sx={{
                                borderRadius: 3,
                                minWidth: 'auto',
                                px: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: 'grey.200',
                                bgcolor: '#ffffff',
                                '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main' }
                            }}
                        >
                            <MdSearch size={22} />
                        </Button>
                    </Stack>
                </Stack>

                {/* Main Table Area */}
                <Paper elevation={2} sx={{ width: '100%', borderRadius: { xs: 2, md: 3 }, overflow: 'hidden' }}>
                    {renderPagination('top')}
                    
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
                        <Table stickyHeader aria-label="slides data table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>Patient ID</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>Patient Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>Slide ID</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>Slide Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>Specimen</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }} align="center">Actions</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }} align="center">Report</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell><Skeleton variant="text" width={60} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={150} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={60} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={150} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={60} /></TableCell>
                                            <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                                            <TableCell align="center"><Skeleton variant="rounded" width={32} height={32} sx={{ mx: 'auto' }} /></TableCell>
                                            <TableCell align="center"><Skeleton variant="rounded" width={90} height={32} sx={{ mx: 'auto' }} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : slideList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No slides found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    slideList.map((i, index) => (
                                        <TableRow key={i.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="500">{i.patient_code}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="500">{i.patient_name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="500">{i.id}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <MuiLink
                                                    component={RouterLink}
                                                    to={`/viewer/${i.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="hover"
                                                    color="primary.main"
                                                    fontWeight="500"
                                                >
                                                    {i.name}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="500">{i.slide_type}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={i.status || 'Unknown'} 
                                                    size="small" 
                                                    color={getStatusColor(i.status)}
                                                    variant={/predicted|completed|analyzed|failed|aborted/i.test(i.status || '')
                                                        ? 'filled' : "outlined"
                                                    }
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Edit Info">
                                                    <IconButton 
                                                        onClick={() => handleOpenEdit(i)} 
                                                        size="small" 
                                                        color="primary"
                                                        sx={{ bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}
                                                    >
                                                        <MdEdit size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="center">
                                                {i.report
                                                    ? <Button
                                                        variant="contained"
                                                        size="small"
                                                        color="primary"
                                                        disabled={downloadingId === i.id}
                                                        startIcon={downloadingId === i.id ? <CircularProgress size={16} /> : <RiDownloadLine size={18} />}
                                                        onClick={() => downloadPDF(i)}
                                                        sx={{ textTransform: 'none', borderRadius: 2, minWidth: 100 }}
                                                    >
                                                        {downloadingId === i.id ? 'Loading...' : 'Ready'}
                                                    </Button>
                                                    : <Tooltip title="Report is currently generating or unavailable" placement="top">
                                                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                                            Not Ready
                                                        </Typography>
                                                    </Tooltip>
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* Edit Info Dialog */}
            <Dialog 
                open={editModalOpen} 
                onClose={handleCloseEdit}
                maxWidth="xs" 
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
                    Edit Patient Details
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            label="Patient ID"
                            fullWidth
                            variant="outlined"
                            value={editFormData.patient_code}
                            onChange={(e) => setEditFormData({ ...editFormData, patient_code: e.target.value })}
                            disabled={isSubmittingEdit}
                        />
                        <TextField
                            label="Patient Name"
                            fullWidth
                            variant="outlined"
                            value={editFormData.patient_name}
                            onChange={(e) => setEditFormData({ ...editFormData, patient_name: e.target.value })}
                            disabled={isSubmittingEdit}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={handleCloseEdit} 
                        color="inherit" 
                        disabled={isSubmittingEdit}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSaveEdit}
                        disabled={isSubmittingEdit}
                        startIcon={isSubmittingEdit ? <CircularProgress size={16} /> : null}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                        {isSubmittingEdit ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {selectedNode && uploadOpen && (
                <UploadSlide
                    open={uploadOpen}
                    closeModal={handleUploadClose}
                    saveFile={saveFile}
                    saveMounts={saveMounts}
                    selectedNode={selectedNode}
                />
            )}
        </Box>
    );
};

export default SimpleDashboard;